import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaTh, FaList, FaAlignJustify } from 'react-icons/fa';
import { api, ApiError } from '../../api/client';
import { API_BASE_URL } from '../../config/api';
import googlePlayImg from '../../assets/images/google-play.png';
import appStoreImg from '../../assets/images/app-store.png';
import {
  DEFAULT_MENU_ITEM_IMAGE,
  flattenActiveMenuWithItems,
  formatMenuItem,
  getMenuItemExtrasMeta,
  getMenuItemUnavailableLabel,
  isMenuItemOrderable,
} from './ourMenuUtils';
import { OurMenuProductDetailModal } from './OurMenuProductDetailModal';
import OurMenuLoadingSkeleton from './OurMenuLoadingSkeleton';
import { useOurMenuProductDetail } from './useOurMenuProductDetail';
import MenuItemActions from './MenuItemActions';
import './OurMenu.css';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.digirasa.client';
const APP_STORE_URL = 'https://apps.apple.com/in/app/digirasa/id6736368836';
const PLAY_STORE_QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(PLAY_STORE_URL)}`;
const APP_STORE_QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(APP_STORE_URL)}`;

/** Quiet "Options" eyebrow + size chips; soft +N add-on pill. */
function MenuItemExtrasHints({ item }) {
  const { hasOptions, optionCount, optionLabels, addOnCount } = getMenuItemExtrasMeta(item);
  if (!hasOptions && addOnCount <= 0) return null;

  const showOptionPills = optionLabels.length > 0 && optionLabels.length <= 3;
  const optionsSummary =
    !showOptionPills && optionCount > 0
      ? `${optionCount} option${optionCount === 1 ? '' : 's'}`
      : null;

  return (
    <div className="menu-item__extras" aria-label="Item customizations">
      {hasOptions ? (
        <div className="menu-item__extras-block">
          <span className="menu-item__extras-label">Options</span>
          <div className="menu-item__extras-row">
            {showOptionPills
              ? optionLabels.map((label) => (
                  <span key={label} className="menu-item__chip menu-item__chip--option">
                    {label}
                  </span>
                ))
              : (
                  <span className="menu-item__chip menu-item__chip--option">{optionsSummary}</span>
                )}
          </div>
        </div>
      ) : null}
      {addOnCount > 0 ? (
        <div className="menu-item__extras-row">
          <span className="menu-item__chip menu-item__chip--addon">
            +{addOnCount} add-on{addOnCount === 1 ? '' : 's'}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// Cycling pastels for color-grid (index-based)
const gridPastels = ['#dce9f8', '#f8dde2', '#ddf0e6', '#f5f0e4'];

function MenuViewControls({ view, setView }) {
  return (
    <div className="menu-view">
      <div className="menu-view-toggle" role="group" aria-label="Menu layout">
        <button
          type="button"
          className={`menu-view-btn${view === 'grid' ? ' menu-view-btn--active' : ''}`}
          onClick={() => setView('grid')}
          aria-label="Grid view"
          aria-pressed={view === 'grid'}
          title="Grid view"
        >
          <FaTh />
        </button>
        <button
          type="button"
          className={`menu-view-btn menu-view-btn--list${view === 'list' ? ' menu-view-btn--active' : ''}`}
          onClick={() => setView('list')}
          aria-label="List view"
          aria-pressed={view === 'list'}
          title="List view"
        >
          <FaList />
        </button>
        <button
          type="button"
          className={`menu-view-btn${view === 'compact' ? ' menu-view-btn--active' : ''}`}
          onClick={() => setView('compact')}
          aria-label="Compact view"
          aria-pressed={view === 'compact'}
          title="Compact view"
        >
          <FaAlignJustify />
        </button>
      </div>
    </div>
  );
}

const OurMenu = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [view, setView] = useState('grid');
  const [menuItems, setMenuItems] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [menuError, setMenuError] = useState('');
  const { selectedItem, openIntent, editSession, openProduct, closeProduct } =
    useOurMenuProductDetail();
  const location = useLocation();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => {
      if (mq.matches) {
        setView((current) => (current === 'list' ? 'grid' : current));
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const searchTerm = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('search') || '').trim().toLowerCase();
  }, [location.search]);

  const loadMenu = useCallback(async () => {
    setLoadingMenu(true);
    setMenuError('');

    try {
      const { data } = await api.get(
        `${API_BASE_URL}/menu/activeMenuWithItems?includeAvailabilityStatus=true`,
      );
      const rows = flattenActiveMenuWithItems(data);
      if (rows.length > 0) {
        setMenuItems(rows.map(formatMenuItem));
      } else {
        setMenuItems([]);
        setMenuError('No menu items are available right now. Please try again later.');
      }
    } catch (err) {
      setMenuItems([]);
      const msg =
        err instanceof ApiError
          ? err.message
          : 'We could not load the menu. Please check your connection and try again.';
      setMenuError(msg);
    } finally {
      setLoadingMenu(false);
    }
  }, []);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  const mainCategoryCounts = useMemo(
    () =>
      menuItems.reduce((acc, item) => {
        const key = item.menuName || 'Menu';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
    [menuItems],
  );

  const categories = useMemo(
    () => ['All', ...Object.keys(mainCategoryCounts)],
    [mainCategoryCounts],
  );

  const filtered = useMemo(
    () =>
      menuItems.filter((item) => {
        const matchesCategory = activeCategory === 'All' || item.menuName === activeCategory;
        if (!matchesCategory) return false;
        if (!searchTerm) return true;

        const haystack =
          `${item.name} ${item.description || ''} ${item.category || ''} ${item.menuName || ''}`.toLowerCase();
        return haystack.includes(searchTerm);
      }),
    [activeCategory, menuItems, searchTerm],
  );

  const visibleGroups = useMemo(() => {
    const grouped = filtered.reduce((acc, item) => {
      const mainCategory = item.menuName || 'Menu';
      const subcategory = item.category || 'General';

      if (!acc[mainCategory]) {
        acc[mainCategory] = {};
      }

      if (!acc[mainCategory][subcategory]) {
        acc[mainCategory][subcategory] = [];
      }

      acc[mainCategory][subcategory].push(item);
      return acc;
    }, {});

    return Object.entries(grouped).map(([mainCategory, subgroups]) => {
      const allItems = Object.values(subgroups).flat();
      const sectionMenuImage = allItems[0]?.sectionMenuImage || '';

      return {
        mainCategory,
        sectionMenuImage,
        total: Object.values(subgroups).reduce((sum, items) => sum + items.length, 0),
        subgroups: Object.entries(subgroups).map(([subcategory, items]) => ({
          subcategory,
          items,
        })),
      };
    });
  }, [filtered]);

  const renderMenuItems = (items) => {
    if (view === 'compact') {
      return (
        <div className="menu-compact">
          {items.map((item) => {
            const orderable = isMenuItemOrderable(item);
            const unavailableLabel = getMenuItemUnavailableLabel(item);
            return (
            <div key={item.id} className="menu-compact__row">
              <img
                src={item.image}
                alt={item.name}
                className="menu-compact__img"
                style={{ cursor: 'pointer' }}
                onClick={() => openProduct(item)}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = item.fallbackImage || DEFAULT_MENU_ITEM_IMAGE;
                }}
              />
              <span
                className="menu-compact__name"
                style={{ cursor: 'pointer' }}
                onClick={() => openProduct(item)}
              >
                {item.name}
              </span>
              <span className="menu-compact__price">
                {item.priceDisplay}
                <MenuItemExtrasHints item={item} />
              </span>
              {orderable ? (
                <MenuItemActions
                  item={item}
                  orderable
                  variant="compact"
                  className="menu-compact__btn"
                  addedClassName="menu-compact__btn--added"
                  planClassName="menu-compact__btn menu-compact__btn--plan"
                  onOpenOptions={openProduct}
                />
              ) : (
                <span className="menu-compact__oos" role="status">
                  {unavailableLabel}
                </span>
              )}
            </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className={view === 'grid' ? 'menu-grid' : 'menu-list'}>
        {items.map((item) => {
          const orderable = isMenuItemOrderable(item);
          const unavailableLabel = getMenuItemUnavailableLabel(item);
          return (
          <div
            key={item.id}
            className={view === 'grid' ? 'menu-card' : 'menu-list-item'}
          >
            <div
              className="menu-card__img"
              style={{ cursor: 'pointer' }}
              onClick={() => openProduct(item)}
            >
              <img
                src={item.image}
                alt={item.name}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = item.fallbackImage || DEFAULT_MENU_ITEM_IMAGE;
                }}
              />
            </div>
            <div className="menu-card__body">
              <h3 style={{ cursor: 'pointer' }} onClick={() => openProduct(item)}>
                {item.name}
              </h3>
              <p className="menu-card__desc">
                {item.description || `${item.category} · Fresh daily · No preservatives`}
              </p>
            </div>
            <div className="menu-card__footer">
              <div className="menu-card__price-block">
                <span className="menu-card__price">{item.priceDisplay}</span>
                <MenuItemExtrasHints item={item} />
              </div>
              <MenuItemActions
                item={item}
                orderable={orderable}
                unavailableLabel={unavailableLabel}
                variant={view === 'list' ? 'list' : 'card'}
                className="menu-card__btn"
                addedClassName="menu-card__btn--added"
                planClassName="menu-card__btn menu-card__btn--plan"
                onOpenOptions={openProduct}
              />
            </div>
          </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="menu-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>OUR MENU</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Our Menu</span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Section */}
      <section className="menu-section" aria-busy={loadingMenu}>
        <div className="container">
          <div className="menu-section-top">
            <div className="section-header menu-section-header">
              <span className="section-tag">Best Food Menu</span>
              <h2 className="section-title">
                OUR BEST <span>FOODS MENUS</span>
              </h2>
              {loadingMenu ? (
                <p className="menu-meta menu-meta--skeleton" aria-hidden>
                  <span className="menu-skeleton menu-skeleton--meta" />
                </p>
              ) : (
                <p className="menu-meta">
                  <strong className="menu-meta__value">{menuItems.length}</strong>{' '}
                  {menuItems.length === 1 ? 'item' : 'items'}
                  <span className="menu-meta__sep" aria-hidden>
                    ·
                  </span>
                  <strong className="menu-meta__value">
                    {Math.max(0, categories.length - 1)}
                  </strong>{' '}
                  {categories.length - 1 === 1 ? 'category' : 'categories'}
                </p>
              )}
            </div>
            <MenuViewControls view={view} setView={setView} />
          </div>

          {loadingMenu ? (
            <>
              <div className="menu-controls">
                <OurMenuLoadingSkeleton part="filters" />
              </div>
              <OurMenuLoadingSkeleton part="body" view={view} />
              <p className="visually-hidden" aria-live="polite">
                Loading menu…
              </p>
            </>
          ) : (
            <>
              <div className="menu-controls">
                <div className="menu-filter" role="toolbar" aria-label="Menu categories">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`menu-filter__btn${activeCategory === cat ? ' menu-filter__btn--active' : ''}`}
                      onClick={() => setActiveCategory(cat)}
                    >
                      <span>{cat}</span>
                      <span className="menu-filter__count">
                        {cat === 'All' ? menuItems.length : mainCategoryCounts[cat] || 0}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {menuError ? (
                <div className="menu-error-banner" role="alert">
                  <p className="menu-error-banner__text">{menuError}</p>
                  <button
                    type="button"
                    className="menu-error-banner__retry"
                    onClick={loadMenu}
                    disabled={loadingMenu}
                  >
                    Try again
                  </button>
                </div>
              ) : null}

              {!menuError && filtered.length === 0 && (
                <p className="section-desc">
                  {searchTerm
                    ? `No menu items found for "${searchTerm}".`
                    : 'No menu items found for this category.'}
                </p>
              )}

              {(view === 'grid' || view === 'list' || view === 'compact') && !menuError && (
                <div className="menu-groups">
                  {visibleGroups.map((group) => (
                    <section key={group.mainCategory} className="menu-group">
                      <div className="menu-group__header">
                        {group.sectionMenuImage ? (
                          <img
                            src={group.sectionMenuImage}
                            alt=""
                            className="menu-group__menu-thumb"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <div className="menu-group__header-main">
                          <h3>{group.mainCategory}</h3>
                          <span>
                            {group.total} item{group.total === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>

                      <div className="menu-subgroups">
                        {group.subgroups.map((subgroup) => (
                          <div
                            key={`${group.mainCategory}-${subgroup.subcategory}`}
                            className="menu-subgroup"
                          >
                            <div className="menu-subgroup__header">
                              <h4>{subgroup.subcategory}</h4>
                              <span>
                                {subgroup.items.length} item{subgroup.items.length === 1 ? '' : 's'}
                              </span>
                            </div>
                            {renderMenuItems(subgroup.items)}
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}

              {view === 'color-grid' && !menuError && (
                <div className="menu-color-grid">
                  {filtered.map((item, i) => {
                    const pastel = gridPastels[i % gridPastels.length];
                    const orderable = isMenuItemOrderable(item);
                    const unavailableLabel = getMenuItemUnavailableLabel(item);
                    return (
                      <div
                        key={item.id}
                        className="menu-ccard"
                        style={{ background: pastel }}
                      >
                        <div
                          className="menu-ccard__img-wrap"
                          style={{ cursor: 'pointer' }}
                          onClick={() => openProduct(item)}
                        >
                          <img
                            src={item.image}
                            alt={item.name}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = item.fallbackImage || DEFAULT_MENU_ITEM_IMAGE;
                            }}
                          />
                        </div>
                        <div className="menu-ccard__body">
                          <h3
                            className="menu-ccard__name"
                            style={{ cursor: 'pointer' }}
                            onClick={() => openProduct(item)}
                          >
                            {item.name}
                          </h3>
                          <span className="menu-ccard__price">
                            {item.priceDisplay}
                            <MenuItemExtrasHints item={item} />
                          </span>
                          <MenuItemActions
                            item={item}
                            orderable={orderable}
                            unavailableLabel={unavailableLabel}
                            variant="color"
                            className="menu-ccard__btn"
                            addedClassName="menu-ccard__btn--added"
                            planClassName="menu-ccard__btn menu-ccard__btn--plan"
                            onOpenOptions={openProduct}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Product Detail Modal */}
      <OurMenuProductDetailModal
        item={selectedItem}
        openIntent={openIntent}
        editSession={editSession}
        onClose={closeProduct}
      />

      {/* App Download */}
      <section className="menu-app-section" id="APPSTORE">
        <div className="container">
          <div className="menu-app__content">
            <span className="section-tag">For more offers &amp; Discounts</span>
            <h2 className="section-title">
              Download Our APP
              <br />
              Order Online
            </h2>
            <p>
              We are a trusted nutritious food brand with 2+ years of experience, delivering
              high-quality traditional foods with a focus on health and authenticity.
            </p>
            <div className="menu-app__actions">
              <div className="menu-app__badges">
                <a
                  href="https://play.google.com/store/apps/details?id=com.digirasa.client"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={googlePlayImg} alt="Google Play" />
                </a>
                <a
                  href="https://apps.apple.com/in/app/digirasa/id6736368836"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={appStoreImg} alt="App Store" />
                </a>
              </div>
              <div className="menu-app__qr-list">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="menu-app__qr"
                  aria-label="Scan QR code for Google Play"
                >
                  <img src={PLAY_STORE_QR_URL} alt="Digirasa Google Play QR code" />
                  <span>Scan for Google Play</span>
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="menu-app__qr"
                  aria-label="Scan QR code for App Store"
                >
                  <img src={APP_STORE_QR_URL} alt="Digirasa App Store QR code" />
                  <span>Scan for App Store</span>
                </a>
              </div>
            </div>
            <p className="menu-app__note">* Free delivery within 2kms radius from the kitchen</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurMenu;
